import {
  AlternateEmail,
  DoorFront,
  Lock,
  LockOpen,
  Person4
} from '@mui/icons-material'
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Icon,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography
} from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import React, { useRef } from 'react'
import { connect } from 'react-redux'
import { NivosModal } from 'components/index'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import {
  Operations as UserAdminOperations,
  operations as userAdminOperations
} from 'states/dux/user-admin/operations'
import { Payload as UserAdminPayload } from 'states/dux/user-admin/types'
import {
  Operations as UserOperations,
  operations as userOperations
} from 'states/dux/user/operations'
import { Payload as UserPayload } from 'states/dux/user/types'
import State from 'states/types'
import { NivosUser, NivosUserGroup } from 'types/user'
import {
  get,
  mapOperationsToDispatchProps,
  objectifyDateStringFields,
  post
} from 'utils/common'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
  user: UserPayload
  userAdmin: UserAdminPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
    user: UserOperations
    userAdmin: UserAdminOperations
  }
}

interface OwnProps {
  selectedUser: NivosUser | undefined
  setSelectedUser: (user: NivosUser | undefined) => void
}

type AppProps = StateProps & DispatchProps & OwnProps

const UserEditModal: React.FC<any> = (props: AppProps) => {
  const { theme, selectedUser, operations, setSelectedUser } = props
  const [permissionChoices, setPermissionChoices] = React.useState<
  NivosUserGroup[]
  >([])

  const [modalLoading, setModalLoading] = React.useState(false)
  const [userPermissions, setPermissions] = React.useState<number[]>([])
  const [userEditData, setEditData] = React.useState({ ...selectedUser })
  const [profileImage, setProfileImage] = React.useState<File>()

  const [password, setPassword] = React.useState('')
  const [showPassword, setShowPassword] = React.useState(false)

  const [errors, setErrors] = React.useState({
    firstName: false,
    lastName: false,
    email: false,
    address: false,
    password: false
  })

  const imageInputRef = useRef<HTMLInputElement>(null)
  const isMobileView = useMediaQuery('(max-width:600px)')

  const ITEM_HEIGHT = 48
  const ITEM_PADDING_TOP = 8

  const _generateFormData = (): FormData => {
    const formData = new FormData()
    if (userEditData !== undefined && userEditData.id === 0) {
      formData.append('username', userEditData.email ?? '')
      formData.append('first_name', userEditData.firstName ?? '')
      formData.append('last_name', userEditData.lastName ?? '')
      formData.append('email', userEditData.email ?? '')
      formData.append('address', userEditData.address ?? '')
      formData.append('groups_id', userPermissions.toString())
      formData.append('password', password)
      if (profileImage !== undefined) {
        formData.append('image', profileImage, profileImage.name)
      }
    }
    return formData
  }

  const _updateUser = (): void => {
    setModalLoading(true)
    if (userEditData.id !== undefined && userEditData.id !== 0) {
      void post(
        `/account/${userEditData?.id}/`,
        {
          first_name: userEditData.firstName,
          last_name: userEditData.lastName,
          groups: userPermissions,
          address: userEditData.address
        },
        false,
        false,
        'PUT'
      )
        .then(data => {
          try {
            operations.userAdmin.update(objectifyDateStringFields(data))
          } catch (e) {
            console.log(e)
          }
        })
        .finally(() => {
          setEditData({})
          setSelectedUser(undefined)
          setModalLoading(false)
        })
    } else if (userEditData.id === 0) {
      const requiredFields = ['firstName', 'lastName', 'email', 'address']
      let errorStates = errors
      let hasErrors = false
      Object.entries(userEditData).forEach(entry => {
        if (
          requiredFields.includes(entry[0]) &&
          (entry[1] === '' ||
            entry[1] === null ||
            entry[1] === undefined ||
            (Array.isArray(entry[1]) && entry[1].length === 0))
        ) {
          errorStates = { ...errorStates, [entry[0]]: true }
          hasErrors = true
        }
      })
      if (password === '') {
        errorStates.password = true
        hasErrors = true
      }
      setErrors(errorStates)
      if (hasErrors) {
        return
      }
      void post('/account/', _generateFormData(), true)
        .then(data => {
          try {
            operations.userAdmin.add(objectifyDateStringFields(data))
          } catch (e) {
            console.log(e)
          }
        })
        .finally(() => {
          setEditData({})
          setSelectedUser(undefined)
          setModalLoading(false)
        })
    }
  }

  React.useEffect(() => {
    if (selectedUser !== undefined) {
      setPermissions(selectedUser.groups.map(item => item.id))
      setEditData({ ...selectedUser })
    }

    void get('/account/group/').then(data => {
      try {
        setPermissionChoices(data)
      } catch (e) {
        console.log(e)
      }
    })
  }, [selectedUser])

  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250
      }
    }
  }

  return (
    <NivosModal
      title={selectedUser?.id !== 0 ? 'Manage User' : 'Create User'}
      open={selectedUser !== undefined}
      setOpen={_ => setSelectedUser(undefined)}
      containerProps={{
        style: { maxWidth: 500 }
      }}
    >
      {selectedUser !== undefined && (
        <Box
          display={'flex'}
          flexDirection={'column'}
          alignItems={'center'}
          minWidth={isMobileView ? 0 : 500}
          height={1}
          paddingY={1}
        >
          <Box
            onClick={() => {
              if (imageInputRef?.current != null) {
                imageInputRef.current.showPicker()
              }
            }}
          >
            <Avatar
              src={
                selectedUser.id === 0
                  ? profileImage !== undefined
                    ? URL.createObjectURL(profileImage)
                    : ''
                  : selectedUser.image?.url
              }
              sx={{
                height: 200,
                width: 200,
                borderRadius: 2,
                marginY: 2,
                cursor: 'pointer'
              }}
            />
            {selectedUser.id === 0 && (
              <input
                ref={imageInputRef}
                type={'file'}
                accept={'image/*'}
                style={{ display: 'none' }}
                onChange={event => {
                  if (event.currentTarget.files != null) {
                    setProfileImage(event.currentTarget.files[0])
                  }
                }}
              />
            )}
            <Divider sx={{ borderColor: theme.secondaryColor }} />
          </Box>
          <Grid container justifyContent={'center'} spacing={1}>
            <Grid item sm={12} md={6}>
              <TextField
                fullWidth
                InputProps={{
                  startAdornment: (
                    <Icon sx={{ marginRight: '5px' }}>
                      <Person4 />
                    </Icon>
                  )
                }}
                onChange={event => {
                  setErrors({ ...errors, firstName: false })
                  setEditData({
                    ...userEditData,
                    firstName: event.currentTarget.value
                  })
                }}
                error={errors.firstName}
                id={'f-name'}
                placeholder={'First name'}
                defaultValue={selectedUser.firstName}
                sx={{ marginY: 1 }}
                disabled={modalLoading}
              />
            </Grid>
            <Grid item sm={12} md={6}>
              <TextField
                fullWidth
                InputProps={{
                  startAdornment: (
                    <Icon sx={{ marginRight: '5px' }}>
                      <Person4 />
                    </Icon>
                  )
                }}
                onChange={event => {
                  setErrors({ ...errors, lastName: false })
                  setEditData({
                    ...userEditData,
                    lastName: event.currentTarget.value
                  })
                }}
                error={errors.lastName}
                id={'l-name'}
                placeholder={'Last name'}
                defaultValue={selectedUser.lastName}
                sx={{ marginY: 1 }}
                disabled={modalLoading}
              />
            </Grid>
            <Grid item sm={12} md={6}>
              <TextField
                fullWidth
                InputProps={{
                  startAdornment: (
                    <Icon sx={{ marginRight: '5px' }}>
                      <DoorFront />
                    </Icon>
                  )
                }}
                onChange={event => {
                  setErrors({ ...errors, address: false })
                  setEditData({
                    ...userEditData,
                    address: event.currentTarget.value
                  })
                }}
                error={errors.address}
                id={'address'}
                placeholder={'Address'}
                defaultValue={selectedUser.address}
                sx={{ marginY: 1 }}
                disabled={modalLoading}
              />
            </Grid>
            <Grid item sm={12} md={6}>
              <TextField
                fullWidth
                InputProps={{
                  startAdornment: (
                    <Icon sx={{ marginRight: '5px' }}>
                      <AlternateEmail />
                    </Icon>
                  )
                }}
                onChange={event => {
                  setErrors({ ...errors, email: false })
                  setEditData({
                    ...userEditData,
                    email: event.currentTarget.value
                  })
                }}
                error={errors.email}
                id={'e-mail'}
                placeholder={'e-mail address'}
                defaultValue={selectedUser.email}
                disabled={selectedUser.id !== 0 || modalLoading}
                sx={{ marginY: 1 }}
              />
            </Grid>
            <Grid item sm={12} md={6}>
              <Select
                multiple
                displayEmpty
                value={userPermissions}
                input={
                  <OutlinedInput
                    sx={{ marginY: 1, width: isMobileView ? 285 : '100%' }}
                  />
                }
                renderValue={selected => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.length === 0 && (
                      <Typography>
                        <em>User Permissions</em>
                      </Typography>
                    )}
                    {selected.map((value, idx) => (
                      <Chip
                        key={idx}
                        label={
                          permissionChoices.filter(i => i.id === value)[0].name
                        }
                      />
                    ))}
                  </Box>
                )}
                inputProps={{ 'aria-label': 'Without label' }}
                onChange={event => {
                  const {
                    target: { value }
                  } = event
                  setPermissions(
                    typeof value === 'string'
                      ? value.split(',').map(i => parseInt(i))
                      : value
                  )
                }}
                MenuProps={MenuProps}
                disabled={modalLoading}
              >
                {permissionChoices.map(permission => (
                  <MenuItem key={permission.id} value={permission.id}>
                    {permission.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item sm={12} md={6}>
              <TextField
                fullWidth
                InputProps={{
                  startAdornment: (
                    <Box onClick={() => setShowPassword(!showPassword)}>
                      <Icon sx={{ marginRight: '5px', cursor: 'pointer' }}>
                        {showPassword && <LockOpen />}
                        {!showPassword && <Lock />}
                      </Icon>
                    </Box>
                  )
                }}
                onChange={event => {
                  setErrors({ ...errors, password: false })
                  setPassword(event.currentTarget.value)
                }}
                error={errors.password}
                id={'password'}
                type={showPassword ? 'text' : 'password'}
                placeholder={'password'}
                value={password}
                disabled={selectedUser.id !== 0 || modalLoading}
                sx={{ marginY: 1 }}
              />
            </Grid>
          </Grid>
          <Box
            display={'flex'}
            justifyContent={'flex-end'}
            paddingTop={5}
            width={0.9}
          >
            <Button
              variant={'contained'}
              size={'large'}
              sx={{ marginX: 1 }}
              onClick={() => _updateUser()}
              disabled={modalLoading}
            >
              {modalLoading && 'Loading..'}
              {!modalLoading && (selectedUser.id !== 0 ? 'Save' : 'Create')}
            </Button>
            <Button
              variant={'contained'}
              color={'error'}
              size={'large'}
              sx={{ marginX: 1 }}
              onClick={() => setSelectedUser(undefined)}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}
    </NivosModal>
  )
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout,
    user: state.user,
    userAdmin: state.userAdmin
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations,
  user: userOperations,
  userAdmin: userAdminOperations
})

const UserAdminUserEditModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(UserEditModal) as React.FC<OwnProps>

export default UserAdminUserEditModal
